import { useState } from 'react'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { DataTable, type DataColumn } from '@/components/ui/DataTable'
import { Input, Label } from '@/components/ui/Input'
import { RowMenu } from '@/components/ui/RowMenu'
import { SectionHeader } from '@/components/ui/SectionHeader'
import { Select } from '@/components/ui/Select'
import { TableSkeleton } from '@/components/ui/Skeleton'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { isPositive } from '@/lib/acquisition'
import { fmtDate, fmtDateTime, fmtDecimal, fmtPct } from '@/lib/format'
import { FEE_KIND_LABEL, FEE_PROVIDER_LABEL } from './FeeLabels'

export interface FeeRule {
  id: number
  provider: string
  kind: string
  pct: string
  fixed: string
  fixed_currency: string
  effective_from: string
  is_estimate: boolean
  note: string | null
  created_by: number | null
  created_at: string | null
  in_force: boolean
}

export interface FeeRuleDraft {
  provider: string
  kind: string
  pct: string
  fixed: string
  fixed_currency: string
  effective_from: string
  note: string
  is_estimate: boolean
}

export interface FeeRulesTableProps {
  rules?: FeeRule[]
  kinds: Record<string, string[]>
  currencies: string[]
  /** accounting.fees: owner / admin / finance */
  canEdit: boolean
  busy?: boolean
  today?: string
  onCreate?: (draft: FeeRuleDraft) => void
  onDelete?: (rule: FeeRule) => void
}

function ruleState(r: FeeRule, today: string): { tone: 'ok' | 'neutral' | 'warn'; label: string } {
  if (r.in_force) return { tone: 'ok', label: 'Действует' }
  if (r.effective_from > today) return { tone: 'warn', label: 'С будущей даты' }
  return { tone: 'neutral', label: 'История' }
}

/** Бухгалтерия → Комиссии: the tariff table per provider with its full history (who and
 *  when), and — for accounting.fees — a form for a new tariff from a date. A rule is never
 *  edited in place: a new one starts on its date; recorded fees keep their snapshot.
 *  Presentational; every state is in Storybook (rule 22). */
export function FeeRulesTable({
  rules,
  kinds,
  currencies,
  canEdit,
  busy,
  today = new Date().toISOString().slice(0, 10),
  onCreate,
  onDelete,
}: FeeRulesTableProps) {
  const providers = Object.keys(kinds)
  const [draft, setDraft] = useState<FeeRuleDraft>({
    provider: providers[0] ?? 'heleket',
    kind: kinds[providers[0] ?? 'heleket']?.[0] ?? 'acquiring',
    pct: '',
    fixed: '0',
    fixed_currency: currencies[0] ?? 'USDT',
    effective_from: today,
    note: '',
    is_estimate: false,
  })
  if (rules === undefined)
    return (
      <Card className="p-0">
        <TableSkeleton cols={6} rows={4} />
      </Card>
    )
  const setProvider = (provider: string) =>
    setDraft({ ...draft, provider, kind: kinds[provider]?.[0] ?? draft.kind })
  const columns: DataColumn<FeeRule>[] = [
    { id: 'from', header: 'С даты', type: 'date', cell: (r) => fmtDate(r.effective_from), mobile: 'meta' },
    { id: 'kind', header: 'Вид', type: 'name', cell: (r) => FEE_KIND_LABEL[r.kind] ?? r.kind, mobile: 'title' },
    { id: 'pct', header: 'Процент', type: 'percent', cell: (r) => fmtPct(r.pct, { digits: 0, max: 4 }), mobile: 'amount' },
    {
      id: 'fixed',
      header: 'Фикс.',
      type: 'money',
      className: 'text-ink-2',
      mobile: 'hidden',
      cell: (r) => (isPositive(r.fixed) ? `${fmtDecimal(r.fixed, { max: 6 })} ${r.fixed_currency ?? ''}`.trim() : '—'),
    },
    {
      id: 'status',
      header: 'Статус',
      type: 'status',
      mobile: 'status',
      cell: (r) => {
        const st = ruleState(r, today)
        return (
          <span className="flex flex-wrap gap-1.5">
            <StatusBadge tone={st.tone}>{st.label}</StatusBadge>
            {r.is_estimate && <StatusBadge tone="warn">Оценка, заменить ставкой договора</StatusBadge>}
          </span>
        )
      },
    },
    { id: 'note', header: 'Основание', cell: (r) => <span className="text-caption">{r.note ?? '—'}</span>, mobile: 'hidden' },
    {
      id: 'who',
      header: 'Кто и когда',
      mobile: 'hidden',
      cell: (r) => (
        <span className="flex flex-col text-caption text-ink-3">
          <span>{r.created_by ? `админ #${r.created_by}` : 'начальная настройка'}</span>
          <span className="tnum">{fmtDateTime(r.created_at)}</span>
        </span>
      ),
    },
    ...(canEdit
      ? [
          {
            id: 'actions',
            header: '',
            type: 'actions' as const,
            cell: (r: FeeRule) => (
              <RowMenu
                label={`Действия с тарифом от ${fmtDate(r.effective_from)}`}
                items={[{ id: 'delete', label: 'Удалить', danger: true, disabled: busy, onSelect: () => onDelete?.(r) }]}
              />
            ),
          },
        ]
      : []),
  ]
  return (
    <div className="flex flex-col gap-4">
      {canEdit && (
        <Card>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Select
              id="feerulestable-f1"
              label="Провайдер"
              value={draft.provider}
              onChange={setProvider}
              options={providers.map((p) => ({ value: p, label: FEE_PROVIDER_LABEL[p] ?? p }))}
            />
            <Select
              id="feerulestable-f2"
              label="Вид комиссии"
              value={draft.kind}
              onChange={(kind) => setDraft({ ...draft, kind })}
              options={(kinds[draft.provider] ?? []).map((k) => ({ value: k, label: FEE_KIND_LABEL[k] ?? k }))}
            />
            <div>
              <Label htmlFor="feerulestable-f3">Процент</Label>
              <Input
                id="feerulestable-f3" inputMode="decimal" value={draft.pct} placeholder="2.0" onChange={(e) => setDraft({ ...draft, pct: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="feerulestable-f4">Действует с</Label>
              <Input
                id="feerulestable-f4" type="date" value={draft.effective_from} onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="feerulestable-f5">Фиксированная часть</Label>
              <Input
                id="feerulestable-f5" inputMode="decimal" value={draft.fixed} onChange={(e) => setDraft({ ...draft, fixed: e.target.value })} />
            </div>
            <Select
              id="feerulestable-f6"
              label="Валюта фиксированной части"
              value={draft.fixed_currency}
              onChange={(fixed_currency) => setDraft({ ...draft, fixed_currency })}
              options={currencies.map((c) => ({ value: c, label: c }))}
            />
            <div>
              <Label htmlFor="feerulestable-f7">Основание</Label>
              <Input
                id="feerulestable-f7" value={draft.note} placeholder="договор, письмо менеджера" onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
            </div>
            <div className="flex flex-col justify-end gap-2">
              <label className="flex items-center gap-2 text-caption text-ink-2">
                <input type="checkbox" checked={draft.is_estimate} onChange={(e) => setDraft({ ...draft, is_estimate: e.target.checked })} />
                Ставка не подтверждена
              </label>
              <Button
                variant="primary"
                loading={busy}
                disabled={!draft.pct || !draft.effective_from}
                onClick={() =>
                  onCreate?.({ ...draft, pct: draft.pct.replace(',', '.'), fixed: (draft.fixed || '0').replace(',', '.') })
                }
              >
                Добавить тариф
              </Button>
            </div>
          </div>
          <p className="mt-2 text-caption text-ink-3">
            Новый тариф начинает действовать с указанной даты, старые остаются в истории. Уже записанные комиссии не
            пересчитываются. Каждое изменение пишется в аудит.
          </p>
        </Card>
      )}
      {providers.map((provider) => {
        const own = rules.filter((r) => r.provider === provider)
        return (
          <section key={provider}>
            <SectionHeader as="h3" title={FEE_PROVIDER_LABEL[provider] ?? provider} />
            {own.length === 0 ? (
              <p className="rounded-md border border-hairline bg-surface-1 p-4 text-caption text-ink-3">
                Тарифа нет: комиссии этого провайдера считаются только по данным самого провайдера
              </p>
            ) : (
              <DataTable label={`Тарифы комиссий: ${FEE_PROVIDER_LABEL[provider] ?? provider}`} rows={own} columns={columns} getRowId={(r) => String(r.id)} />
            )}
          </section>
        )
      })}
    </div>
  )
}
